import React, { useState, useEffect } from 'react';
import { 
  Instagram, 
  MapPin, 
  Phone, 
  Star, 
  ChevronDown, 
  Clock, 
  Utensils,
  Leaf,
  Sun,
  Navigation,
  MessageCircle,
  Menu,
  X
} from 'lucide-react';

// Restaurant Logo Component
const Logo = ({ className = "w-24 h-24" }) => (
  <img 
    src="/logo.png" 
    alt="Raíces de Pichi Mallay Logo" 
    className={`${className} object-contain`}
  />
);

// WhatsApp URL
const WHATSAPP_URL = "https://wa.me/56979652775?text=Hola,%20me%20gustaría%20hacer%20una%20reserva%20en%20Raíces%20de%20Pichi%20Mallay";
const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Tril+Tril+Rio+Sur+Maicolpue+San+Juan+de+la+Costa";
const INSTAGRAM_URL = "https://www.instagram.com/raices.pichimallay";

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-ocean-950 font-inter">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-ocean-950/95 backdrop-blur-md shadow-lg py-2' 
          : 'bg-transparent py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <Logo className="w-12 h-12 md:w-14 md:h-14" />
              <span className={`font-playfair text-white text-lg md:text-xl font-semibold transition-opacity duration-300 ${
                isScrolled ? 'opacity-100' : 'opacity-0'
              }`}>
                Raíces de Pichi Mallay
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#inicio" className="text-white/80 hover:text-wood-400 transition-colors text-sm font-medium">
                Inicio
              </a>
              <a href="#experiencia" className="text-white/80 hover:text-wood-400 transition-colors text-sm font-medium">
                Experiencia
              </a>
              <a href="#ubicacion" className="text-white/80 hover:text-wood-400 transition-colors text-sm font-medium">
                Ubicación
              </a>
              <a href="#contacto" className="text-white/80 hover:text-wood-400 transition-colors text-sm font-medium">
                Contacto
              </a>
              <a 
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-wood-500 hover:bg-wood-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105 shadow-lg"
              >
                Reservar
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          <div className={`md:hidden transition-all duration-300 overflow-hidden ${
            mobileMenuOpen ? 'max-h-64 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}>
            <div className="flex flex-col gap-4 pb-4">
              <a href="#inicio" className="text-white/80 hover:text-wood-400 transition-colors text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                Inicio
              </a>
              <a href="#experiencia" className="text-white/80 hover:text-wood-400 transition-colors text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                Experiencia
              </a>
              <a href="#ubicacion" className="text-white/80 hover:text-wood-400 transition-colors text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                Ubicación
              </a>
              <a href="#contacto" className="text-white/80 hover:text-wood-400 transition-colors text-sm font-medium" onClick={() => setMobileMenuOpen(false)}>
                Contacto
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="inicio" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('/img/exp2.jpg')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-ocean-950/70 via-ocean-900/60 to-ocean-950/90"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          {/* Logo */}
          <div className="mb-6 flex justify-center animate-fade-in">
            <Logo className="w-28 h-28 md:w-36 md:h-36 drop-shadow-2xl" />
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 mb-6">
            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
            <span className="text-white/90 text-sm font-medium">4.4 Estrellas en Google</span>
            <span className="text-white/60 text-sm">(65+ Opiniones)</span>
          </div>

          {/* Headline */}
          <h1 className="font-playfair text-4xl sm:text-5xl md:text-7xl lg:text-8xl text-white font-bold mb-4 text-shadow leading-tight">
            Raíces de Pichi Mallay
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl md:text-2xl text-white/90 font-light mb-8 max-w-2xl mx-auto text-shadow-sm">
            Una experiencia única con hermosa vista al mar en San Juan de la Costa
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a 
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-wood-500 hover:bg-wood-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all hover:scale-105 shadow-xl flex items-center gap-3"
            >
              <MessageCircle className="w-5 h-5 group-hover:animate-pulse" />
              Reservar por WhatsApp
            </a>
            <a 
              href="#experiencia"
              className="text-white/80 hover:text-white border border-white/30 hover:border-white/60 px-8 py-4 rounded-full text-lg font-medium transition-all flex items-center gap-2"
            >
              Descubrir más
              <ChevronDown className="w-5 h-5 animate-bounce" />
            </a>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Experience Section */}
      <section id="experiencia" className="relative py-20 md:py-32 bg-ocean-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <span className="text-wood-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
                Nuestra Esencia
              </span>
              <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl text-white font-bold mb-6 leading-tight">
                Una experiencia gastronómica junto al mar
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Ubicado camino a Tril Tril, ofrecemos productos frescos del mar en un entorno inigualable. 
                Contamos con asientos al aire libre y platos vegetarianos para todos los gustos.
              </p>

              {/* Features */}
              <div className="grid sm:grid-cols-2 gap-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="bg-ocean-800 p-3 rounded-xl">
                    <Utensils className="w-6 h-6 text-wood-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Mariscos Frescos</h4>
                    <p className="text-white/60 text-sm">Directos del mar a tu mesa</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-ocean-800 p-3 rounded-xl">
                    <Leaf className="w-6 h-6 text-wood-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Opciones Vegetarianas</h4>
                    <p className="text-white/60 text-sm">Platos para todos los gustos</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-ocean-800 p-3 rounded-xl">
                    <Sun className="w-6 h-6 text-wood-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Terraza al Aire Libre</h4>
                    <p className="text-white/60 text-sm">Vista panorámica al océano</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="bg-ocean-800 p-3 rounded-xl">
                    <Star className="w-6 h-6 text-wood-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">Experiencia Única</h4>
                    <p className="text-white/60 text-sm">Un lugar escondido especial</p>
                  </div>
                </div>
              </div>

              {/* Important Notice */}
              <div className="bg-gradient-to-r from-wood-600/20 to-wood-500/10 border border-wood-500/30 rounded-2xl p-6">
                <div className="flex items-start gap-4">
                  <div className="bg-wood-500 p-2 rounded-full">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="text-wood-400 font-semibold text-lg mb-2">
                      ⚠️ Atención exclusivamente con reservas anticipadas
                    </h4>
                    <p className="text-white/70 text-sm">
                      Para garantizar la mejor experiencia, trabajamos únicamente con reservas previas. 
                      Contáctanos por WhatsApp para asegurar tu mesa.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Image Grid */}
            <div className="order-1 lg:order-2 grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/img/plato1.jpg"
                    alt="Arrecife"
                    className="w-full h-48 md:h-64 object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/img/plato2.jpg"
                    alt="Caldillo de congrio"
                    className="w-full h-32 md:h-48 object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>
              <div className="space-y-4 pt-8">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/img/plato3.jpg"
                    alt="Camarones al pil pil"
                    className="w-full h-32 md:h-48 object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img 
                    src="/img/plato4.jpg"
                    alt="Empanadas de mariscos"
                    className="w-full h-48 md:h-64 object-cover hover:scale-110 transition-transform duration-500"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Section */}
      <section id="ubicacion" className="relative py-20 md:py-32 bg-ocean-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-wood-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
              Encuéntranos
            </span>
            <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl text-white font-bold mb-4">
              Nuestra Ubicación
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Ubicados en un rincón especial de la costa chilena, donde el mar y la naturaleza se encuentran.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-stretch">
            {/* Map Placeholder */}
            <div className="rounded-2xl overflow-hidden shadow-2xl bg-ocean-800 h-80 lg:h-auto min-h-[320px] relative">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3029.1267748331484!2d-73.75777031452228!3d-40.60502649769251!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x963d995a47983ef7%3A0xe8370f3b179650db!2sRestaurant%20Raices%20de%20Pichi%20Mallay!5e0!3m2!1ses-419!2scl!4v1766614055789!5m2!1ses-419!2scl"
                width="100%" 
                height="100%" 
                style={{ border: 0, minHeight: '320px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Ubicación Raíces de Pichi Mallay"
                className="grayscale hover:grayscale-0 transition-all duration-500"
              />
            </div>

            {/* Location Details */}
            <div className="bg-ocean-800/50 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-ocean-700">
              <div className="space-y-8">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="bg-wood-500/20 p-3 rounded-xl">
                    <MapPin className="w-6 h-6 text-wood-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-2">Dirección</h4>
                    <p className="text-white/70 leading-relaxed">
                      Tril Tril - Rio Sur<br />
                      Maicolpue<br />
                      San Juan de la Costa, Los Lagos
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="bg-wood-500/20 p-3 rounded-xl">
                    <Phone className="w-6 h-6 text-wood-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-2">Teléfono</h4>
                    <a 
                      href="tel:+56979652775" 
                      className="text-white/70 hover:text-wood-400 transition-colors text-lg"
                    >
                      +56 9 7965 2775
                    </a>
                  </div>
                </div>

                {/* Instagram */}
                <div className="flex items-start gap-4">
                  <div className="bg-wood-500/20 p-3 rounded-xl">
                    <Instagram className="w-6 h-6 text-wood-400" />
                  </div>
                  <div>
                    <h4 className="text-white font-semibold text-lg mb-2">Instagram</h4>
                    <a 
                      href={INSTAGRAM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white/70 hover:text-wood-400 transition-colors text-lg"
                    >
                      @raices.pichimallay
                    </a>
                  </div>
                </div>

                {/* CTA Button */}
                <a 
                  href='https://maps.app.goo.gl/LzoadEi5E86AWR7M7'
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-ocean-700 hover:bg-ocean-600 text-white py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 border border-ocean-600"
                >
                  <Navigation className="w-5 h-5" />
                  Cómo llegar
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 md:py-32 bg-ocean-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-wood-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
              Galería
            </span>
            <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl text-white font-bold">
              Momentos en Raíces
            </h2>
          </div>

{/* --- INICIO GALERÍA ADAPTADA (3 Verticales + 1 Horizontal) --- */}
<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">

  {/* 1. FOTO FAMILIA (Vertical) */}
  <div className="h-96 md:h-[500px] overflow-hidden rounded-2xl shadow-lg relative group">
    <img 
      src="/img/exp1.jpg" 
      alt="Familia en Raíces" 
      // object-center suele funcionar bien si el contenedor es alto
      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" 
    />
  </div>

  {/* 2. FOTO BARCO DÍA (Vertical) */}
  <div className="h-96 md:h-[500px] overflow-hidden rounded-2xl shadow-lg relative group">
    <img 
      src="/img/exp2.jpg" 
      alt="Barco de madera de día" 
      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" 
    />
  </div>

  {/* 3. FOTO PIRATA (Vertical) */}
  <div className="h-96 md:h-[500px] overflow-hidden rounded-2xl shadow-lg relative group">
    <img 
      src="/img/exp3.jpg" 
      alt="Estatua Pirata" 
      // Al ser vertical el contenedor, el pirata saldrá entero
      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" 
    />
  </div>

{/* 4. FOTO BARCO NOCHE (Horizontal - MUCHO MÁS ALTA) */}
  {/* Hemos aumentado la altura a 450px en escritorio para ver si mejora la nitidez */}
  <div className="md:col-span-3 h-96 md:h-[450px] overflow-hidden rounded-2xl shadow-lg relative group">
    <img 
      src="/img/exp4.jpg" 
      alt="Barco iluminado de noche" 
      // Mantenemos el foco al centro
      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" 
    />
  </div>

</div>
{/* --- FIN GALERÍA --- */}
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" className="bg-ocean-950 border-t border-ocean-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            {/* Logo & Tagline */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-3">
                <Logo className="w-14 h-14" />
                <span className="font-playfair text-white text-xl font-semibold">
                  Raíces de Pichi Mallay
                </span>
              </div>
              <p className="text-white/60 text-sm">
                Una experiencia gastronómica única en la costa del sur de Chile.
              </p>
            </div>

            {/* Social Links */}
            <div className="flex justify-center gap-4">
              <a 
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-ocean-800 hover:bg-wood-500 text-white p-3 rounded-full transition-all hover:scale-110"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a 
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-ocean-800 hover:bg-wood-500 text-white p-3 rounded-full transition-all hover:scale-110"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
              <a 
                href="tel:+56979652775"
                className="bg-ocean-800 hover:bg-wood-500 text-white p-3 rounded-full transition-all hover:scale-110"
              >
                <Phone className="w-5 h-5" />
              </a>
            </div>

            {/* Contact Info */}
            <div className="text-center md:text-right">
              <p className="text-white/60 text-sm mb-1">
                San Juan de la Costa, Los Lagos
              </p>
              <p className="text-white/60 text-sm">
                +56 9 7965 2775
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-ocean-800 mt-8 pt-8 text-center">
            <p className="text-white/40 text-sm">
              © 2025 Raíces de Pichi Mallay. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-ocean-950 via-ocean-950/95 to-transparent md:hidden z-40">
        <a 
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full bg-wood-500 hover:bg-wood-600 text-white py-4 rounded-full text-lg font-semibold shadow-xl flex items-center justify-center gap-3"
        >
          <MessageCircle className="w-5 h-5" />
          Reservar por WhatsApp
        </a>
      </div>

      {/* Spacer for sticky button on mobile */}
      <div className="h-20 md:hidden"></div>
    </div>
  );
}

export default App;
